/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {AbstractControl, ReactiveFormsModule} from '@angular/forms';
import {TextInputComponent} from '../text-input/text-input.component';
import {EnumInputComponent} from '../enum-input/enum-input.component';
import {NumberInputComponent} from '../number-input/number-input.component';
import {DateInputComponent} from '../date-input/date-input.component';
import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {DocumentInputComponent} from '../document-input/document-input.component';
import {EmailInputComponent} from '../email-input/email-input.component';
import {BooleanInputComponent} from '../boolean-input/boolean-input.component';
import {LanguageTextInputComponent} from '../language-text-input/language-text-input.component';
import {AddressInputComponent} from '../address-input/address-input.component';
import {NameIdInputComponent} from '../name-id-input/name-id-input.component';
import {InputListTextComponent} from '../input-list-text/input-list-text.component';
import {ImageInputComponent} from '../image-input/image-input.component';
import {LanguageSelectInputComponent} from '../language-select-input/language-select-input.component';
import {isLanguageColumn} from '../../../../../../shared/pass-viewer/utils/language-column.util';
import {takeUntil} from 'rxjs';
import {UiComponentDTO} from '../../../../../../../models/Schema';
import {NormalisedApiError} from '../../../../../../../models/errors/api-error';

@Component({
  selector: 'app-input-list',
  imports: [
    CommonModule,
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    TextInputComponent,
    EnumInputComponent,
    NumberInputComponent,
    DateInputComponent,
    FieldLabelComponent,
    DocumentInputComponent,
    EmailInputComponent,
    BooleanInputComponent,
    LanguageTextInputComponent,
    AddressInputComponent,
    NameIdInputComponent,
    InputListTextComponent,
    ImageInputComponent,
    LanguageSelectInputComponent,
  ],
  templateUrl: './input-list.component.html',
  styleUrl: '../input-components.scss',
})
export class InputListComponent
  extends BaseFormFieldDirective
  implements OnInit, OnChanges
{
  /** UI component definitions forwarded from the form builder. */
  @Input() uiComponents: UiComponentDTO[] = [];

  /** Map of normalized valuePaths to their configured UI components. */
  uiComponentForAttribute: Map<string, UiComponentDTO> = new Map();

  /** Whether any server validation error targets this list or one of its items. */
  hasError = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.buildUiComponentMap();
    this.passportService.onPassportUpdateSuccess
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.hasError = false;
          this.control.setErrors(null);
        }
      });
    this.passportService.onUnprocessableEntityError
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        if (!error) {
          this.hasError = false;
        }
      });
  }

  /**
   * Rebuilds the uiComponentForAttribute map whenever relevant inputs change.
   * @param changes HashMap of changes
   */
  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (
      changes['attribute'] ||
      changes['uiComponents'] ||
      changes['fullValuePath']
    ) {
      this.buildUiComponentMap();
    }
  }

  override onControlValueInitialized() {
    super.onControlValueInitialized();
    if (!this.passData) {
      this.passData = [];
    }
  }

  protected readonly Array = Array;

  /** Whether a sub-attribute is the language column of a `list:language` row (rendered as a language select). */
  protected readonly isLanguageColumn = isLanguageColumn;

  /**
   * Marks the list as invalid when the server returns a validation error targeting
   * this list field or any of its items (e.g. `labeling.labels` or
   * `labeling.labels[0].labelingSymbol`).
   *
   * @param normalisedApiError - The normalized error received from the backend.
   * @param control - The form control.
   * @param fullValuePath - The dot-notation path of this list field.
   */
  protected override clearErrors(): void {
    super.clearErrors();
    this.hasError = false;
  }

  override processUnprocessableEntityErrors(
    normalisedApiError: NormalisedApiError,
    control: AbstractControl,
    fullValuePath: string,
  ): void {
    // Reset first, then re-evaluate all entries.
    this.hasError = false;
    this.applyListErrors(normalisedApiError, control, fullValuePath);

    // Also let the base class handle errors on individual child fields.
    super.processUnprocessableEntityErrors(
      normalisedApiError,
      control,
      fullValuePath,
    );
  }

  /**
   * Iterates the normalised error entries and sets `hasError` when any entry
   * targets this list directly or one of its items. Extracted to avoid multiple
   * `break` statements in one loop (SonarQube S135).
   */
  private applyListErrors(
    normalisedApiError: NormalisedApiError,
    control: AbstractControl,
    fullValuePath: string,
  ): void {
    for (const entry of normalisedApiError.all) {
      const code = entry.code ?? '';
      const jsonPathField = this.normalizeFieldPath(
        entry.source?.pointer ?? '',
      );

      const isExactMatch = jsonPathField === fullValuePath;
      const isItemMatch =
        jsonPathField.startsWith(`${fullValuePath}[`) ||
        jsonPathField.startsWith(`${fullValuePath}.`);
      const isAncestorMatch =
        fullValuePath.startsWith(jsonPathField) && this.isAttributeRequired();

      if (isExactMatch || isItemMatch || isAncestorMatch) {
        this.hasError = true;
        this.setControlError(control, isItemMatch, isAncestorMatch, code);
        return;
      }
    }
  }

  /**
   * Sets a server error on the control when no error is already present.
   * Item-level matches use ARRAY_CONTAIN_ERROR; ancestor
   * matches use the MISSING_REQUIRED_PROPERTY property error of the ancestor.
   */
  private setControlError(
    control: AbstractControl,
    isItemMatch: boolean,
    isAncestorMatch: boolean,
    code: string,
  ): void {
    if (isItemMatch && !control.errors) {
      control.setErrors({serverError: 'ARRAY_CONTAIN_ERROR'});
    }
    if (isAncestorMatch && !control.errors) {
      control.setErrors({serverError: code});
    }
  }

  addListItem(listItem: any = {}) {
    if (!this.passData) {
      this.passData = [];
    }
    if (Array.isArray(this.passData)) {
      this.passData.push(listItem);
    }
  }

  async removeItem(index: number) {
    if (
      this.passData &&
      Array.isArray(this.passData) &&
      this.passData.length > 0
    ) {
      this.passData = this.passData.filter(
        (_: unknown, ind: number) => ind !== index,
      );
    }
    await this.onControlValueChanged(this.passData, this.saveFunction);
  }

  /**
   * Rebuilds the uiComponentForAttribute map from the current attributes and uiComponents.
   * Called whenever uiComponents changes so that the template can perform faster map lookups
   * instead of iterating uiComponents on every change detection cycle.
   */
  private buildUiComponentMap(): void {
    const map = new Map<string, UiComponentDTO>();
    const listAttributes = this.attribute?.attributes ?? [];
    for (const listAttribute of listAttributes) {
      const normalizedPath = this.normalizePath(
        `${this.fullValuePath}.${listAttribute.valuePath}`,
      );
      const component = this.getUiComponentForField(normalizedPath);
      if (component) {
        map.set(normalizedPath, component);
      }
    }
    this.uiComponentForAttribute = map;
  }

  private normalizePath(path: string): string {
    return path.replace(/\[\d+\]/g, '');
  }

  /**
   * Returns the UI component whose `reference` matches the given
   * full item path, or `undefined` if no component is configured for it.
   *
   * Array indices are stripped before comparison so that references without indices
   * still match runtime paths containing indices.
   */
  private getUiComponentForField(
    normalizedPath: string,
  ): UiComponentDTO | undefined {
    return this.uiComponents.find(
      (component) => component.reference === normalizedPath,
    );
  }
}
